import {
  DigestPeriod,
  NotificationLevel,
  NotificationSettings,
  NotificationType,
} from "@dashboard-v3/api";
import { AlertFields, FormState, LevelField } from "./types";

export const notificationTypes: NotificationType[] = [
  "MAIL",
  "STORAGE",
  "RULE",
  "PLATFORM",
];

const notificationLevels: NotificationLevel[] = ["INFO", "WARN", "ERROR"];

export const defaultSettings = (user: string): NotificationSettings => ({
  legacy: false,
  mailAddresses: [user],
  monthlyUsageReport: false,
  selectors: notificationTypes.flatMap(type =>
    notificationLevels.map(level => ({
      notificationType: type,
      notificationLevel: level,
      digestPeriod: "HOUR" as const,
    }))
  ),
});

export const toFormState =
  (settings: NotificationSettings) => (): FormState => {
    const { monthlyUsageReport } = settings;
    const mailAddresses = { value: settings.mailAddresses || [], error: "" };

    return {
      mailAddresses,
      monthlyUsageReport,
      alertsByType: {} as AlertFields,
    };
  };

export function initAlerts(selectors: NotificationSettings["selectors"]) {
  const defaults: AlertFields = {
    MAIL: defaultAlertLevel(),
    PLATFORM: defaultAlertLevel(),
    RULE: defaultAlertLevel(),
    STORAGE: defaultAlertLevel(),
  };

  selectors.forEach(s => {
    const levels = defaults[s.notificationType];
    const level = levels.find(l => l.notificationLevel === s.notificationLevel);
    if (level) {
      level.checked = true;
      level.digestPeriod = s.digestPeriod;
    }
  });

  return defaults;
}

function defaultAlertLevel(): LevelField[] {
  return notificationLevels.map(level => ({
    notificationLevel: level,
    checked: false,
    digestPeriod: "HOUR" as DigestPeriod,
  }));
}

export function isTypeActive(levels: LevelField[]): boolean {
  return levels.some(l => l.checked);
}

export const toNotificationSettings = (form: FormState) => {
  const { mailAddresses, monthlyUsageReport, alertsByType } = form;
  const flatSelectors = Object.entries(alertsByType).flatMap(
    ([type, levels]) => {
      return levels
        .filter(l => l.checked)
        .map(l => ({
          notificationType: type,
          notificationLevel: l.notificationLevel,
          digestPeriod: l.digestPeriod,
        }));
    }
  );

  return {
    mailAddresses: mailAddresses.value,
    monthlyUsageReport,
    selectors: flatSelectors,
    legacy: false,
  };
};
